/**
 * Ranked wars, one copy for the website's War tab (Faction → War), the Discord bot (its count of our members' war hits)
 * and the server (the estimates it hands members for both rosters). Pure, browser-safe.
 *
 * Torn's answers read here (v1, the viewer's own key, from the browser, when they open the page or a war):
 *  - /faction/?selections=basic,rankedwars — the faction's ranked wars (scores, start, end, winner), in the same call the
 *    Faction page already makes;
 *  - /torn/{warId}?selections=rankedwarreport — a finished war's report: every member of both sides with their attacks
 *    and score.
 * The bot reads v2 /faction/attacks (the faction's own key, a member who gave it one) — it already does for retals and
 * bonus hits — and counts our members' war hits from it while a member has the live view open during a war.
 */

import type { EstimateMethod } from './player-db';

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x) ? Number(x) : null);
const str = (x: unknown): string | null => (typeof x === 'string' && x.trim() ? x.trim() : null);
/** A map by id ({ "12": {...} }) or a list ([{ id: 12, ... }]) → [id, value] pairs. */
const entries = (x: unknown): Array<[number, Obj]> => {
  if (Array.isArray(x)) return x.flatMap((v) => (obj(v) && num(obj(v)!.id ?? obj(v)!.ID) ? [[num(obj(v)!.id ?? obj(v)!.ID)!, obj(v)!] as [number, Obj]] : []));
  return Object.entries(obj(x) ?? {}).flatMap(([k, v]) => (obj(v) && num(k) ? [[num(k)!, obj(v)!] as [number, Obj]] : []));
};

// ---------------------------------------------------------------- the faction's ranked wars

export interface WarSide {
  id: number;
  name: string;
  score: number;
  chain: number | null;
}

export interface RankedWarInfo {
  id: number;
  /** Unix s. */
  start: number;
  /** Unix s; null while it runs. */
  end: number | null;
  target: number | null;
  winner: number | null;
  factions: WarSide[];
}

/**
 * v1 /faction/?selections=rankedwars (a map by war id), v2 /faction/rankedwars (a list), or v1 `basic`'s `ranked_wars`
 * (the war going on, any faction's — the war room reads the enemy's with the viewer's key): newest first.
 */
export function readRankedWarList(body: unknown): RankedWarInfo[] {
  const out: RankedWarInfo[] = [];
  for (const [id, w] of entries(obj(body)?.rankedwars ?? obj(body)?.ranked_wars)) {
    const war = obj(w.war) ?? w;
    const start = num(war.start);
    if (!start) continue;
    const end = num(war.end);
    const factions = entries(w.factions).map(([fid, f]): WarSide => ({ id: fid, name: str(f.name) ?? `Faction ${fid}`, score: num(f.score) ?? 0, chain: num(f.chain) }));
    out.push({ id, start, end: end && end > 0 ? end : null, target: num(war.target), winner: num(war.winner) || null, factions });
  }
  return out.sort((a, b) => b.start - a.start);
}

/** Running (started, not ended), starting later, or over — at `now` (unix s). */
export function warPhase(w: Pick<RankedWarInfo, 'start' | 'end'>, now: number): 'soon' | 'on' | 'over' {
  if (w.end && w.end <= now) return 'over';
  return w.start > now ? 'soon' : 'on';
}

// ---------------------------------------------------------------- a finished war's report

export interface WarReportMember {
  id: number;
  name: string;
  level: number | null;
  attacks: number;
  /** Torn's war score for the member (respect earned in the war). */
  score: number;
}

export interface WarReportSide {
  id: number;
  name: string;
  score: number;
  attacks: number;
  members: WarReportMember[];
  /** The faction's rank before and after the war ("Gold III" → "Gold II"), when Torn says. */
  rank?: { before: string | null; after: string | null } | null;
  /** What the faction got for the war, when Torn says. */
  rewards?: { respect: number | null; points: number | null; items: Array<{ id: number; name: string; quantity: number }> } | null;
}

export interface WarReport {
  id: number;
  start: number | null;
  end: number | null;
  winner: number | null;
  forfeit: boolean;
  factions: WarReportSide[];
}

/** v2's report adds each side's rank before/after and its rewards (respect, points, items). */
function sideExtras(f: Obj): Pick<WarReportSide, 'rank' | 'rewards'> {
  const rank = obj(f.rank);
  const rw = obj(f.rewards);
  const items = rw
    ? (Array.isArray(rw.items) ? rw.items.map((x, i) => [num(obj(x)?.id) ?? i, x] as const) : Object.entries(obj(rw.items) ?? {}).map(([k, x]) => [num(k) ?? 0, x] as const))
        .flatMap(([id, x]) => {
          const it = obj(x);
          const q = num(it?.quantity) ?? (typeof x === 'number' ? x : null);
          return q && q > 0 ? [{ id: num(it?.id) ?? id, name: str(it?.name) ?? `Item ${num(it?.id) ?? id}`, quantity: q }] : [];
        })
        .slice(0, 50)
    : [];
  return {
    ...(rank ? { rank: { before: str(rank.before), after: str(rank.after) } } : {}),
    ...(rw ? { rewards: { respect: num(rw.respect), points: num(rw.points), items } } : {}),
  };
}

/** v1 /torn/{warId}?selections=rankedwarreport (or v2's report): both sides, each member's attacks and score. */
export function readWarReport(body: unknown, warId: number): WarReport | null {
  const b = obj(body);
  const r = obj(b?.rankedwarreport) ?? obj(b?.report);
  if (!r) return null;
  const war = obj(r.war) ?? r;
  const factions = entries(r.factions)
    .map(([fid, f]): WarReportSide => ({
      id: fid,
      name: str(f.name) ?? `Faction ${fid}`,
      score: num(f.score) ?? 0,
      attacks: num(f.attacks) ?? 0,
      members: entries(f.members)
        .map(([mid, m]): WarReportMember => ({ id: mid, name: str(m.name) ?? `#${mid}`, level: num(m.level), attacks: num(m.attacks) ?? 0, score: Math.round((num(m.score) ?? 0) * 100) / 100 }))
        .sort((x, y) => y.score - x.score || y.attacks - x.attacks),
      ...sideExtras(f),
    }))
    .sort((x, y) => y.score - x.score);
  if (!factions.length) return null;
  const winner = num(war.winner) ?? num(obj(r.winner)?.id);
  return { id: num(r.id) ?? warId, start: num(war.start), end: num(war.end), winner: winner || null, forfeit: war.forfeit === true || r.forfeit === true, factions };
}

// ---------------------------------------------------------------- our members' war hits (the bot's count)

/** One of our members in the current war, from the faction's attack log as the bot reads it. */
export interface WarHitsMember {
  id: number;
  name: string;
  /** Successful attacks on the enemy (Torn's war hits). */
  hits: number;
  /** Respect from them. */
  respect: number;
  /** Assists on the enemy. */
  assists: number;
  /** Every attack on the enemy, lost or not. */
  attacks: number;
}

/** In the live view (LiveView.warHits): counted since `since` (unix s) — the bot counts from when it starts reading. */
export interface WarHits {
  warId: number;
  since: number;
  at: number;
  /** Why it can't count (no key with the faction's API access), else null. */
  problem: string | null;
  members: WarHitsMember[];
}

/** What the count needs of one attack (the bot's reading of v2 /faction/attacks). */
export interface TallyAttack {
  id: number;
  ended: number;
  attacker: { id: number | null; name: string | null; factionId: number | null } | null;
  defender: { factionId: number | null };
  result: string;
  respectGain: number;
}

/** The bot's running count for one war (kept in memory; seen ids so a page read twice isn't counted twice). */
export interface WarTally {
  warId: number;
  since: number;
  seen: Set<number>;
  members: Map<number, WarHitsMember>;
}

/** Results Torn counts as a won attack. */
const WON = /^(attacked|mugged|hospitali[sz]ed|arrested|looted|special|bounty)$/i;

export function newWarTally(warId: number, since: number): WarTally {
  return { warId, since, seen: new Set(), members: new Map() };
}

/**
 * Adds attacks to the count: only ours (attacker in `us`) on the enemy (defender in `enemy`) that ended once the war
 * started; each attack once.
 */
export function tallyWarAttacks(t: WarTally, attacks: readonly TallyAttack[], us: number, enemy: number, warStart: number): void {
  for (const a of attacks) {
    if (t.seen.has(a.id) || a.ended < warStart || a.attacker?.factionId !== us || a.defender.factionId !== enemy || !a.attacker.id) continue;
    t.seen.add(a.id);
    const m = t.members.get(a.attacker.id) ?? { id: a.attacker.id, name: a.attacker.name ?? `#${a.attacker.id}`, hits: 0, respect: 0, assists: 0, attacks: 0 };
    if (a.attacker.name) m.name = a.attacker.name;
    m.attacks++;
    if (/^assist$/i.test(a.result)) m.assists++;
    else if (WON.test(a.result) || a.respectGain > 0) {
      m.hits++;
      m.respect = Math.round((m.respect + Math.max(0, a.respectGain)) * 100) / 100;
    }
    t.members.set(m.id, m);
  }
}

/** The count as the live view shows it: most respect first. */
export function warHitsView(t: WarTally, at: number, problem: string | null): WarHits {
  return { warId: t.warId, since: t.since, at, problem, members: [...t.members.values()].map((m) => ({ ...m })).sort((a, b) => b.respect - a.respect || b.hits - a.hits || a.name.localeCompare(b.name)) };
}

// ---------------------------------------------------------------- estimates for the rosters

/** Omni-Torn's best estimate of a player's battle stats total (the player database's, GET /api/faction/war/estimates). */
export interface WarEstimate {
  id: number;
  total: number;
  source: EstimateMethod | string;
  /** ms */
  at: number;
}

export interface WarEstimatesAnswer {
  estimates: WarEstimate[];
}

/** At most this many ids in one ask (two full rosters). */
export const WAR_ESTIMATE_MAX_IDS = 200;
